import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { NatService } from 'src/app/services/nat.service';
import { ToastrService } from 'ngx-toastr';
import { ImportWizardStorage, TabularColumns } from '@/app/pages/project/map/repository/import-wizard/import-wizard.storage';
import { ImportWizardTabularService } from '@/app/pages/project/map/repository/import-wizard/components/tabular/import-wizard-tabular.service';

export type TabularNatsResponseInterface = {
    status: string;
    rows_validated: number;
    total_rows: number;
    progress_percentage: number;
    errors_found: number;
};

export interface TabularValidationStartOptions {
    importId: string;
    columns: TabularColumns[];
}

@Injectable()
export class ImportWizardTabularValidationHandler implements OnDestroy {

    private readonly _progress$ = new Subject<number>();
    private readonly _subs = new Subscription();

    readonly progress$: Observable<number> = this._progress$.asObservable();

    constructor(
        private readonly natsService: NatService,
        private readonly importWizardTabularService: ImportWizardTabularService,
        private readonly importWizardStorage: ImportWizardStorage,
        private readonly toastrService: ToastrService,
    ) {}

    init(importId: string): void {
        this._subscribeNats(importId);
    }

     /**
     * Inicia o fluxo completo de validação:
     * 1. Dispara o POST de validação
     * 2. Assina o tópico NATS para acompanhar o progresso
     * 3. Ao chegar em 100%:
     *    - sucesso → setNextStep()
     *    - erro    → busca erros, exibe warning e setNextStep()
     *
     * @param importId  ID do import wizard em andamento
     * @param columns   Colunas a serem salvas no storage
     */
    start({ importId, columns }: TabularValidationStartOptions): void {
        const sub = this.importWizardTabularService
            .postValidateImportWizard(importId)
            .subscribe({
                next: () => {
                    this.importWizardStorage.setDisplayColumns(columns);
                },
                error: (err) => {
                    console.error('[TabularValidationHandler] Erro ao iniciar validação:', err);
                },
            });

        this._subs.add(sub);
    }

    ngOnDestroy(): void {
        this._progress$.complete();
        this._subs.unsubscribe();
    }

    private _subscribeNats(importId: string): void {
        const topic = `import_wizard.${importId}.validation`;
        this.natsService.subscribeToTopic(topic, (msg: string) => {
            this._handleNatsMessage(msg);
        });
    }

    private _handleNatsMessage(msg: string): void {
        try {
            const result: TabularNatsResponseInterface = JSON.parse(msg);
            this._progress$.next(result.progress_percentage);

            if (result.progress_percentage === 100) {
                result.errors_found > 0
                    ? this.toastrService.warning('Foram encontrados erros na importação!')
                    : this.toastrService.success('Importação validada com sucesso!');

                this.importWizardStorage.setNextStep();
            }
        } catch (error) {
            console.error('[TabularValidationHandler] Erro ao processar mensagem NATS:', error);
        }
    }
}